import { API_BASE, setToken } from './client.ts';

/**
 * 브라우저에서 하는 카카오 로그인 (인가 코드 방식) — 화면 A1.
 *
 * 네이티브 셸이 있을 때는 셸이 토큰을 심어 줍니다. 셸 없이 브라우저에서 열면
 * 그 길이 없으므로, 표준 OAuth 로 직접 로그인합니다.
 *
 *   1. 서버의 /auth/kakao/start 로 **화면을 이동**합니다.
 *      fetch 가 아니라 이동이라 CORS 가 걸리지 않고, REST 키도 이 번들에
 *      들어오지 않습니다 — 주소는 서버가 만듭니다.
 *   2. 카카오가 로그인을 끝내면 `?code=...&state=...` 를 달고 돌아옵니다.
 *   3. 그 코드를 서버에 넘기면 우리 토큰이 나옵니다.
 *
 * `state` 는 여기서 만들어 sessionStorage 에 두었다가 돌아왔을 때 맞춰 봅니다.
 * 남이 만든 코드를 내 브라우저에서 쓰게 만드는 일(로그인 CSRF)을 막는 값입니다.
 */

const STATE_KEY = 'prayerlog.oauth-state';

function newState(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** 카카오 로그인 화면으로 보냅니다. 돌아오지 않는 함수입니다 */
export function startKakaoLogin(): void {
  const state = newState();
  try {
    sessionStorage.setItem(STATE_KEY, state);
  } catch {
    // 프라이빗 모드 등에서 막힐 수 있습니다. 그때는 state 확인을 건너뜁니다 —
    // 로그인 자체를 못 하게 하는 것보다는 낫습니다
  }
  window.location.href = `${API_BASE}/auth/kakao/start?state=${encodeURIComponent(state)}`;
}

/** 돌아온 주소에 인가 코드가 실려 있는가 */
export function hasAuthCode(): boolean {
  return new URLSearchParams(window.location.search).has('code');
}

/**
 * 돌아온 코드로 로그인을 끝냅니다.
 *
 * 성공하든 실패하든 **주소에서 코드를 지웁니다.** 남겨 두면 새로고침할 때마다
 * 같은 코드가 다시 날아가는데, 인가 코드는 한 번 쓰면 죽으므로 그때부터는
 * 계속 실패합니다.
 */
export async function finishKakaoLogin(): Promise<void> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const state = params.get('state');
  const error = params.get('error');

  cleanUrl();

  if (error !== null) {
    // 사용자가 카카오 화면에서 «취소»를 누른 경우가 대부분입니다
    throw new Error('카카오 로그인이 취소되었습니다.');
  }
  if (code === null) throw new Error('인가 코드가 없습니다.');

  let expected: string | null = null;
  try {
    expected = sessionStorage.getItem(STATE_KEY);
    sessionStorage.removeItem(STATE_KEY);
  } catch {
    /* 못 읽으면 아래에서 건너뜁니다 */
  }
  // 저장을 못 했으면 expected 가 없습니다. 그때는 확인을 건너뜁니다
  if (expected !== null && expected !== state) {
    throw new Error('로그인 요청이 확인되지 않았습니다. 다시 시도해 주세요.');
  }

  const res = await fetch(`${API_BASE}/auth/kakao/web`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });

  const data = (await res.json().catch(() => ({}))) as { token?: string; error?: string };
  if (!res.ok || !data.token) {
    throw new Error(data.error ?? '로그인에 실패했습니다. 다시 시도해 주세요.');
  }

  setToken(data.token);
}

/** 코드·state·error 를 주소에서 지웁니다. 해시(라우터)는 그대로 둡니다 */
function cleanUrl(): void {
  try {
    window.history.replaceState(null, '', window.location.pathname + window.location.hash);
  } catch {
    /* 지우지 못해도 로그인 자체는 진행됩니다 */
  }
}
